// @flow
import React from 'react';
import moment from 'moment';
import { get, first, toArray, size, toNumber, isNil } from 'lodash';
import { sort, dateCompare, createNsTranslator, alphabeticCompare, handleReportPeriods } from '@helpers/helpers';
import FinancialItemCard from '../../../blocks/PatientFinancial/FinancialItemCard';
import { Icon, Button, Loading, FormattedText, Table } from '@elements';
import { ReportsMenu, ReportPage, PlatformSwitch, AppMobileHeader, EmptyState, ReportsFilters, ConditionalFeature, BlockedReport, AppMobileFooter, ServiceQuantityLine } from '@blocks';
import type { DoctorsState, ServicesState, FormElementData, TableColumItems, ReportPeriodFilter } from '@types';
import { withStorage } from '@hocs';
import { withTranslation } from 'react-i18next';
import ReportPagination from '../ReportPagination';
import ServiceService from '../../../../services/ServiceService';
import './ReportsServices.scss';

type Props = {
  onGetServices: (startDate: string, endDate: string, doctorId?: number | null, status?: string, periodFilter?: ReportPeriodFilter, page?: number) => void,
  doctors: DoctorsState,
  services: ServicesState,
  componentStorage: Object,
  onItemClick: Function,
  onDownloadReport: (startDate: string, endDate: string, doctorId?: number | null, status?: string, periodFilter?: ReportPeriodFilter) => void,
  defaultDoctorId: number,
  isManager: boolean,
  isFeatureEnabled: boolean,
  reportsSelectDoctor: boolean,
  t: Function,
  isReportBlocked: boolean,
  servicesByQuantity: boolean,
};

type State = {
  period: string,
  doctor: string,
  year: number,
  month: number,
  status: string,
  showMobileFilters: boolean,
  startDate: any,
  endDate: any,
};

class ReportsServices extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    const state = {
      period: 'today',
      doctor: this.props.defaultDoctorId || get(first(toArray(get(this.props.doctors, 'data'))), 'id'),
      month: moment().month() + 1,
      year: moment().year(),
      status: 'confirmed',
      startDate: moment().startOf('month').format('YYYY-MM-DD'),
      endDate: moment().endOf('month').format('YYYY-MM-DD'),
      ...props.componentStorage.get('filters', {}),
    };
    this.state = {
      ...state,
      // the free date-range period was removed; fall back for filters stored before that
      period: state.period === 'custom' ? 'today' : state.period,
      month: parseInt(state.month, 10) > 0 ? state.month : moment().month() + 1,
    };
  }

  componentDidMount() {
    if (this.props.isFeatureEnabled) {
      this.handleRequestData();
    }
  }

  render() {
    const {
      doctors, services, isManager, isFeatureEnabled, reportsSelectDoctor, isReportBlocked, servicesByQuantity,
    } = this.props;
    const {
      period, doctor, year, month, status, showMobileFilters,
    } = this.state;


    const _ = createNsTranslator('reports', this.props.t);

    if (isReportBlocked) {
      return (
        <BlockedReport
          backLinkTo="/relatorios"
          title={_('services/title')}
        />
      );
    }

    const isLoading = get(services, 'isLoading', false);
    const isLoadingReport = get(services, 'isLoadingReport', false);
    // once a report is requested, downloading stays disabled until the
    // filters change (see the services reducer)
    const isDownloadDisabled = isLoadingReport || get(services, 'reportRequested', false);

    // Guard against potential null objects
    if (!services && this.props.isFeatureEnabled) return false;
    const summary = sort(get(services, 'summary', []), alphabeticCompare('name'));
    const servicesData = sort(get(services, ['data'], {}), dateCompare('date', 'desc'));

    const paginationControls = () => (
      <ReportPagination
        meta={get(services, 'meta', null)}
        isLoading={isLoading}
        onPageChange={this.handlePageChange}
        itemsName={_('pagination/services')}
      />
    );



    // the extra Unidades column leaves less room, so amounts stay on one line
    const amountCell = servicesByQuantity ? 'noWrapCell' : undefined;
    const sumaryColumns: TableColumItems = [
      {
        dataIndex: 'name',
        content: _('label/service', 'global'),
        onRenderItem: (item) => (servicesByQuantity ? (
          <React.Fragment>
            <span className="reportServiceName">{item.name}</span>
            {item.unit && <span className="serviceQuantityLine">{this.props.t('financial:label/per_unit', { unit: item.unit })}</span>}
          </React.Fragment>
        ) : item.name),
      },
      {
        dataIndex: 'count',
        content: servicesByQuantity ? _('services/sales') : _('label/quantity'),
      },
      ...(servicesByQuantity ? [{
        dataIndex: 'sumQuantity',
        content: _('services/units'),
        rowClass: amountCell,
        onRenderItem: (item) => (item.unit ? ServiceService.quantityWithUnit(item.sumQuantity, item.unit) : '—'),
      }] : []),
      {
        dataIndex: 'sumPrices',
        content: _('label/total'),
        rowClass: amountCell,
        onRenderItem: (item) => <FormattedText type="currency" text={item.sumPrices} />,
      },
      {
        dataIndex: 'averagePrice',
        content: servicesByQuantity ? _('services/average_unit_value') : _('label/average_value'),
        rowClass: amountCell,
        onRenderItem: (item) => (
          // A template sold by unit has no price per unit in a period where none of its sales was by quantity
          <FormattedText type="currency" text={servicesByQuantity && item.unit && !isNil(item.averageUnitPrice) ? item.averageUnitPrice : item.averagePrice} />
        ),
      },
      {
        dataIndex: 'templatePrice',
        content: _('label/table_value'),
        rowClass: amountCell,
        onRenderItem: (item) => <FormattedText type="currency" text={item.templatePrice} />,
      },
    ];

    const servicesColumns: TableColumItems = [
      {
        dataIndex: 'date',
        content: _('label/date', 'global'),
        onRenderItem: (service) => <FormattedText type="date" text={service.date} />,
      },
      {
        dataIndex: 'patientName',
        content: _('label/patient', 'global'),
      },
      {
        dataIndex: 'name',
        content: _('label/service', 'global'),
        onRenderItem: (service) => (servicesByQuantity ? (
          <React.Fragment>
            <span className="reportServiceName">{service.name}</span>
            <ServiceQuantityLine service={service} servicesByQuantity={servicesByQuantity} />
          </React.Fragment>
        ) : service.name),
      },
      {
        dataIndex: 'doctorName',
        content: _('label/doctor', 'global'),
        hidden: doctor !== 'all',
      },
      {
        dataIndex: 'averagePrice',
        content: _('label/value', 'global'),
        onRenderItem: (service) => <FormattedText type="currency" text={service.price} />,
      },
      {
        dataIndex: 'id',
        content: <React.Fragment>&nbsp;</React.Fragment>,
        className: 'actionCell',
        desktopOnly: true,
        onRenderItem: () => <span className="linkAction">{_('action/view_patient', 'global')}</span>,
      },
    ];

    // The app's cards (the patient financial lists' FinancialItemCard). A service in the summary: its total,
    // and under it the sales, units and the average and table values. A service sold: the patient and its value.
    // [key, text] pairs, shown with " · " between them
    const joined = (parts) => parts.map(([key, part], index) => <React.Fragment key={key}>{index > 0 && ' · '}{part}</React.Fragment>);
    const summaryCard = (item) => (
      <FinancialItemCard
        title={item.name}
        value={<FormattedText type="currency" text={item.sumPrices} />}
        details={joined(servicesByQuantity ? [
          ['sales', `${_('services/sales')}: ${item.count}`],
          ...(item.unit ? [['units', `${_('services/units')}: ${ServiceService.quantityWithUnit(item.sumQuantity, item.unit)}`]] : []),
          ['average', <React.Fragment>{_('services/average_unit_value')}: <FormattedText type="currency" text={item.unit && !isNil(item.averageUnitPrice) ? item.averageUnitPrice : item.averagePrice} /></React.Fragment>],
        ] : [
          ['quantity', `${_('label/quantity')}: ${item.count}`],
          ['average', <React.Fragment>{_('label/average_value')}: <FormattedText type="currency" text={item.averagePrice} /></React.Fragment>],
          ['table', <React.Fragment>{_('label/table_value')}: <FormattedText type="currency" text={item.templatePrice} /></React.Fragment>],
        ])}
      />
    );
    const serviceCard = (service, { onClick }) => (
      <FinancialItemCard
        title={service.patientName}
        value={<FormattedText type="currency" text={service.price} />}
        details={(
          <React.Fragment>
            {service.name}
            <ServiceQuantityLine service={service} servicesByQuantity={servicesByQuantity} />
          </React.Fragment>
        )}
        date={service.date}
        doctorName={doctor !== 'all' ? null : service.doctorName}
        onClick={onClick}
      />
    );

    const isAllEmpty = size(summary) === 0 && size(servicesData) === 0;


    return (
      <PlatformSwitch
        className="platformSwitcher"
        desktop={() => (
          <div id="reportsServicesPage" className="appContent reportsPage">
            <ReportsMenu />

            <section className="mainSection">
              <ConditionalFeature enabled={isFeatureEnabled} transparent>
                <div className="centerContent">
                  <div className="toolbar">
                    <h1>{_('services/title')}</h1>
                    <div className="actions">
                      <Button disabled={isAllEmpty || isDownloadDisabled} onClick={this.handleDownloadReport}>
                        <Icon iconName="download" className="icon" size={18} />
                        <span>{_('action/download_report', 'global')}</span>
                      </Button>
                    </div>
                  </div>

                  <div className="desktopCard">
                    <ReportsFilters
                      reportType="services"
                      reportsSelectDoctor={reportsSelectDoctor}
                      isManager={isManager}
                      doctors={doctors}
                      isLoading={isLoading}
                      onChangeFilter={this.handleFilterChange}
                      doctor={doctor}
                      period={period}
                      year={year}
                      month={month}
                      status={status}
                    />
                    <React.Fragment>
                      {isAllEmpty && !isLoading ?
                        <EmptyState onReportFilter={this.toggleMobileFilter} title={_('services/empty_title')} hint={_('services/empty_hint')} />
                        :
                        <React.Fragment>
                          {!isLoading && size(summary) > 0 &&
                          <section>
                            <div className="toolbar">
                              <h2>Resumo</h2>
                            </div>
                            <Table
                              itemKey="date"
                              columns={sumaryColumns}
                              fullTable
                              isLoading={isLoading}
                              items={summary}
                              emptyMessage={_('services/empty_title')}
                            />
                          </section>
                          }

                          <section>
                            {!isLoading &&
                              <div className="toolbar">
                                <h2>{_('services/hired_list')}</h2>
                              </div>
                            }
                            {isLoading && <Loading />}
                            {!isLoading &&
                            <Table
                              itemKey="id"
                              columns={servicesColumns}
                              fullTable
                              isLoading={isLoading}
                              items={servicesData}
                              mobileClickLabel={_('action/view_patient', 'global')}
                              emptyMessage={_('services/empty_title')}
                              onItemClick={this.onItemClick}
                            />
                            }
                            {paginationControls()}
                          </section>
                        </React.Fragment>
                    }
                    </React.Fragment>
                  </div>
                </div>
              </ConditionalFeature>
            </section>
          </div>
        )}
        mobile={() => (
          <React.Fragment>
            <AppMobileHeader title={_('services/title')} backLinkTo="/relatorios">
              <div className="headerActions">
                {!isAllEmpty && !isDownloadDisabled && (
                  <Icon iconName="download" className="icon" onClick={this.handleDownloadReport} />
                )}
                {(!isAllEmpty && isDownloadDisabled) && (
                  <Icon iconName="download" className="icon" />
                )}
                <Icon iconName="filter" className="icon" onClick={this.toggleMobileFilter} />
                {/* <Icon iconName="refresh" className="icon" onClick={this.onRefreshClick} /> */}
              </div>
            </AppMobileHeader>
            <ReportPage
              id="reportsServicesPage"
              filters={() => showMobileFilters && (
                <ReportsFilters
                  isMobile
                  reportType="services"
                  reportsSelectDoctor={reportsSelectDoctor}
                  isManager={isManager}
                  doctors={doctors}
                  isLoading={isLoading}
                  onChangeFilter={this.handleFilterChange}
                  doctor={doctor}
                  period={period}
                  year={year}
                  month={month}
                  status={status}
                  onCloseMobileModal={this.toggleMobileFilter}
                />)}
              isFeatureEnabled={isFeatureEnabled}
              isLoading={isLoading}
              emptyState={() => isAllEmpty && (
                <EmptyState
                  onReportFilter={this.toggleMobileFilter}
                  title={_('services/empty_title')}
                  hint={_('services/empty_hint')}
                />)}
              noPadding
            >
              {!isLoading && size(summary) > 0 &&
                <ReportPage.Table title="Resumo">
                  <Table
                    itemKey="date"
                    columns={sumaryColumns}
                    fullTable
                    isLoading={isLoading}
                    items={summary}
                    emptyMessage={_('services/empty_title')}
                    renderMobileItem={summaryCard}
                  />
                </ReportPage.Table>
              }
              <ReportPage.Table title={_('services/hired_list')}>
                <Table
                  itemKey="id"
                  columns={servicesColumns}
                  fullTable
                  isLoading={isLoading}
                  items={servicesData}
                  mobileClickLabel={_('action/view_patient', 'global')}
                  emptyMessage={_('services/empty_title')}
                  onItemClick={this.onItemClick}
                  renderMobileItem={serviceCard}
                />
                {paginationControls()}
              </ReportPage.Table>
            </ReportPage>
            <AppMobileFooter />
          </React.Fragment>
        )}
      />
    );
  }

  handleFilterChange = (data: FormElementData) => {
    if (data.name === 'month') {
      let m = parseInt(get(data, 'value', ''), 10);
      m = m > 0 ? m : moment().month() + 1;
      m = m <= 12 ? m : moment().month() + 1;
      this.setState({ [data.name]: m }, this.handleRequestData);
    } else {
      this.setState({ [data.name]: get(data, 'value', '') }, this.handleRequestData);
    }
  };

  // page defaults to 1, so every filter change resets to the first page
  handleRequestData = (page: number = 1) => {
    const {
      doctor, period, month, year, status, startDate, endDate,
    } = this.state;
    this.props.componentStorage.set('filters', { ...this.state, showMobileFilters: false });
    const doctorFilter = doctor !== 'all' ? toNumber(doctor) : null;

    const reportPeriod = handleReportPeriods(startDate, endDate, period, month, year);

    this.props.onGetServices(
      reportPeriod.startDate,
      reportPeriod.endDate,
      doctorFilter, status,
      { period, month, year },
      page,
    );
  };

  handlePageChange = (page: number) => this.handleRequestData(page);

  handleDownloadReport = () => {
    const {
      doctor, period, month, year, status, startDate, endDate,
    } = this.state;
    this.props.componentStorage.set('filters', { ...this.state, showMobileFilters: false });

    const doctorFilter = doctor !== 'all' ? toNumber(doctor) : null;

    const reportPeriod = handleReportPeriods(startDate, endDate, period, month, year);

    this.props.onDownloadReport(
      reportPeriod.startDate,
      reportPeriod.endDate,
      doctorFilter, status,
      { period, month, year },
    );
  };

  onRefreshClick = () => {
    this.handleRequestData();
  };

  toggleMobileFilter = () => this.setState({ showMobileFilters: !this.state.showMobileFilters });

  onItemClick = (service: any) => this.props.onItemClick(service.patientId);
}


export default withTranslation()(withStorage('reportsFiltersV3', 'reportsServices')(ReportsServices));
